// Shared vocabulary for the manager's side of Checklists. The words are deliberately plain —
// "Nobody showed up" beats "missed", because that's what a manager would actually say out loud.
export const STATUS_WORDS = {
  scheduled: { label: "Not started", tone: "mute" },
  in_progress: { label: "In progress", tone: "info" },
  submitted: { label: "Needs your review", tone: "amber" },
  needs_attention: { label: "Waiting on the vendor", tone: "amber" },
  vendor_replied: { label: "Vendor replied", tone: "info" },
  carried: { label: "Unfinished — carried over", tone: "amber" },
  approved: { label: "Approved", tone: "ok" },
  missed: { label: "Nobody showed up", tone: "bad" },
  excused: { label: "Excused", tone: "mute" },
};

export const TONE_CLS = {
  ok: "text-emerald-700",
  amber: "text-amber-700",
  bad: "text-rose-700",
  info: "text-indigo-700",
  mute: "text-muted-foreground",
};

export const NEEDS_YOU = ["submitted", "vendor_replied", "missed"];

export const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

export const pretty = (iso, opts = {}) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y) return iso || "";
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US",
    { weekday: "long", month: "long", day: "numeric", timeZone: "UTC", ...opts });
};

export const dayHeading = (iso, todayIso) => {
  if (iso === todayIso) return `Today · ${pretty(iso)}`;
  const t = new Date(`${todayIso}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() - 1);
  if (iso === t.toISOString().slice(0, 10)) return `Yesterday · ${pretty(iso)}`;
  return pretty(iso);
};

// Deadlines are stored 24-hour and read back the way a person says them. "23:30" -> "11:30pm".
export const clock = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  if (!m) return hhmm || "";
  const h = Number(m[1]);
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m[2] === "00" ? `${h12}${suffix}` : `${h12}:${m[2]}${suffix}`;
};

export const cadence = (tpl) => {
  const freq = tpl.freq || "weekly";
  const days = (tpl.dow || []).map((d) => (DAYS.find((x) => x[0] === d) || [])[1]).filter(Boolean);
  const n = Number(tpl.interval) || (freq === "every_n_weeks" ? 2 : 1);
  if (freq === "every_n_days") return n === 1 ? "Every day" : `Every ${n} days`;
  if (freq === "monthly_day") return `Monthly on the ${ordinal(Number(tpl.month_day) || 1)}`;
  if (freq === "monthly_nth") {
    const w = (DAYS.find((x) => x[0] === (tpl.nth_dow || (tpl.dow || [])[0])) || ["", "Mon"])[1];
    return `The ${(NTHS.find((x) => x[0] === Number(tpl.nth || 1)) || [1, "1st"])[1]} ${w} of the month`;
  }
  if (!days.length) return "No days set";
  if (freq === "every_n_weeks") return `Every ${n} weeks on ${days.join(", ")}`;
  return days.length === 7 ? "Every day" : days.join(", ");
};

// ── recurrence, mirrored from backend/checklists.py occurs_on() ──────────────
// The server is the only thing that actually creates visits; this exists so the builder can
// show a manager the next few dates before they save.
export const FREQS = [
  ["weekly", "Every week"],
  ["every_n_weeks", "Every few weeks"],
  ["monthly_day", "Monthly, on a date"],
  ["monthly_nth", "Monthly, on a weekday"],
  ["every_n_days", "Every few days"],
];
export const NTHS = [[1, "1st"], [2, "2nd"], [3, "3rd"], [4, "4th"], [-1, "last"]];
export const NEEDS_DAYS = ["weekly", "every_n_weeks"];

export const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"][((n % 100) - 20) % 10] || ["th", "st", "nd", "rd"][n % 100] || "th";
  return `${n}${s}`;
};

const toDate = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  return y && m && d ? new Date(Date.UTC(y, m - 1, d)) : null;
};
const isoOf = (dt) => dt.toISOString().slice(0, 10);
const dowOf = (dt) => DAYS[(dt.getUTCDay() + 6) % 7][0];
const mondayOf = (dt) => new Date(dt.getTime() - ((dt.getUTCDay() + 6) % 7) * 864e5);
const lastDayOf = (dt) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0)).getUTCDate();

export const occursOn = (tpl, dt) => {
  const iso = isoOf(dt);
  if ((tpl.skip_dates || []).includes(iso)) return false;
  if (tpl.start_date && tpl.start_date > iso) return false;
  if (tpl.end_date && iso > tpl.end_date) return false;
  const freq = tpl.freq || "weekly";
  const dk = dowOf(dt);
  if (NEEDS_DAYS.includes(freq)) {
    if (!(tpl.dow || []).includes(dk)) return false;
    if (freq === "weekly") return true;
    const n = Math.max(1, Math.min(12, Number(tpl.interval) || 2));
    const a = mondayOf(toDate(tpl.start_date) || toDate(tpl.created_at) || new Date(Date.UTC(1970, 0, 5)));
    const weeks = Math.round((mondayOf(dt) - a) / (7 * 864e5));
    return weeks >= 0 && weeks % n === 0;
  }
  if (freq === "every_n_days") {
    const n = Math.max(1, Math.min(180, Number(tpl.interval) || 1));
    const gap = Math.round((dt - (toDate(tpl.start_date) || toDate(tpl.created_at) || new Date(Date.UTC(1970, 0, 5)))) / 864e5);
    return gap >= 0 && gap % n === 0;
  }
  const last = lastDayOf(dt);
  if (freq === "monthly_day") {
    return dt.getUTCDate() === Math.min(Math.max(1, Math.min(31, Number(tpl.month_day) || 1)), last);
  }
  if (dk !== (tpl.nth_dow || (tpl.dow || [])[0] || "mon")) return false;
  const nth = Number(tpl.nth || 1);
  if (nth === -1) return dt.getUTCDate() + 7 > last;
  return Math.floor((dt.getUTCDate() - 1) / 7) + 1 === nth;
};

export const nextDates = (tpl, fromIso, count = 3, span = 200) => {
  const start = toDate(fromIso);
  const out = [];
  if (!start) return out;
  for (let i = 0; i < span && out.length < count; i += 1) {
    const dt = new Date(start.getTime() + i * 864e5);
    if (occursOn(tpl, dt) && hasTasksOn(tpl, dowOf(dt))) out.push(isoOf(dt));
  }
  return out;
};

// Mirrors tasks_for_day(): a day every one of whose tasks is scoped elsewhere is not a visit.
const hasTasksOn = (tpl, dk) => {
  const tasks = tpl.tasks || [];
  if (!tasks.length) return true;
  return tasks.some((t) => !Array.isArray(t.days) || t.days.includes(dk));
};
